// Composant Jauge Circulaire Réutilisable
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

interface GaugeChartProps {
  score: number;
  maxScore: number;
  label: string;
  size?: 'small' | 'medium' | 'large';
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const GaugeCircle = styled.div<{ score: number; maxScore: number; size: string }>`
  width: ${props => {
    switch (props.size) {
      case 'small': return '120px';
      case 'large': return '180px';
      default: return '160px';
    }
  }};
  height: ${props => {
    switch (props.size) {
      case 'small': return '120px';
      case 'large': return '180px';
      default: return '160px';
    }
  }};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: ${theme.borderRadius.full};
    background: conic-gradient(
      from 180deg,
      ${props => {
        const percentage = (props.score / props.maxScore) * 100;
        const angle = (percentage * 3.6) - 90;
        if (percentage >= 80) return `${theme.colors.success} 0deg, ${theme.colors.success} ${angle}deg, #E0E0E0 ${angle}deg`;
        if (percentage >= 60) return `${theme.colors.warning} 0deg, ${theme.colors.warning} ${angle}deg, #E0E0E0 ${angle}deg`;
        return `${theme.colors.error} 0deg, ${theme.colors.error} ${angle}deg, #E0E0E0 ${angle}deg`;
      }}
    );
    mask-image: radial-gradient(
      circle,
      transparent 0%,
      transparent 60%,
      black 60%,
      black 100%
    );
    -webkit-mask-image: radial-gradient(
      circle,
      transparent 0%,
      transparent 60%,
      black 60%,
      black 100%
    );
  }

  @media print {
    width: auto;
    height: auto;
    display: inline-block;

    &::before {
      display: none;
    }
  }
`;

const GaugeNeedle = styled.div<{ score: number; maxScore: number }>`
  position: absolute;
  width: 4px;
  height: 60px;
  background: ${theme.colors.text};
  border-radius: ${theme.borderRadius.full};
  bottom: 50%;
  left: 50%;
  transform-origin: bottom center;
  transform: translateX(-50%) rotate(${props => {
    const percentage = (props.score / props.maxScore) * 100;
    return (percentage * 1.8) - 90;
  }}deg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 1s ease-out;

  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background: ${theme.colors.text};
    border: 3px solid white;
    border-radius: ${theme.borderRadius.full};
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  @media print {
    display: none;
  }
`;

const ScoreValue = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`;

const ScoreNumber = styled.div<{ score: number; maxScore: number }>`
  font-size: ${theme.typography.sizes['4xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${props => {
    const percentage = (props.score / props.maxScore) * 100;
    if (percentage >= 80) return theme.colors.success;
    if (percentage >= 60) return theme.colors.warning;
    return theme.colors.error;
  }};
  line-height: 1;
`;

const Label = styled.div`
  text-align: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.surface}80;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  white-space: nowrap;
`;

export const GaugeChart: React.FC<GaugeChartProps> = React.memo(({
  score,
  maxScore,
  label,
  size = 'medium'
}) => {
  return (
    <Container>
      <GaugeCircle score={score} maxScore={maxScore} size={size}>
        <ScoreValue>
          <ScoreNumber score={score} maxScore={maxScore}>
            {score}
          </ScoreNumber>
        </ScoreValue>
        <GaugeNeedle score={score} maxScore={maxScore} />
      </GaugeCircle>
      <Label>{label}</Label>
    </Container>
  );
});
